import { useState } from "react";
import { useQuery } from "react-query";
import { format } from "date-fns";
import { RefreshCw, Search, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const actions = ["all", "create", "update", "delete", "approve", "reject", "export", "send"];

export default function AuditLogs() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");

  const { data, isLoading, isFetching, refetch } = useQuery(
    ["audit-logs-page", page, search, action],
    async () => {
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      if (search.trim()) params.set("search", search.trim());
      if (action !== "all") params.set("action", action);
      const response = await api.get(`/audit-logs?${params.toString()}`);
      return response.data;
    },
    { keepPreviousData: true, staleTime: 15000 }
  );

  const logs = data?.results || data?.data || [];
  const total = Number(data?.total || logs.length || 0);
  const pageCount = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="space-y-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">Audit Logs</h1>
          <p className="mt-1 text-sm text-slate-500">Trace financial, approval, account, and administrative actions.</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 md:grid-cols-[1fr_220px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                className="pl-9"
                placeholder="Search user, target, action, or details..."
              />
            </div>
            <Select value={action} onValueChange={(value) => { setAction(value); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                {actions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item === "all" ? "All Actions" : item.charAt(0).toUpperCase() + item.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Target</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3" colSpan={5}><Skeleton className="h-5 w-full" /></td>
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr>
                    <td className="px-4 py-14 text-center text-slate-500" colSpan={5}>
                      <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                      No audit logs found.
                    </td>
                  </tr>
                ) : (
                  logs.map((log: any) => (
                    <tr key={log.id} className="hover:bg-slate-50/70">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {log.createdAt ? format(new Date(log.createdAt), "dd MMM yyyy HH:mm") : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="capitalize">{log.action || "-"}</Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {log.targetType || log.entityType || "-"} {log.targetId ? `#${log.targetId}` : ""}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {log.userName || log.actorName || log.userEmail || log.userId || "System"}
                      </td>
                      <td className="max-w-[420px] truncate px-4 py-3 text-slate-500">
                        {log.description || log.details || log.message || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>Showing page {page} of {pageCount} ({total.toLocaleString()} logs)</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
